import { cva, type VariantProps } from 'class-variance-authority';
import { type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/*
 * Alert renders an inline message block with a clear tonal hierarchy.
 * The `tone` variant maps directly to the design-system colour tokens:
 * info, success (teal), warning, danger.
 *
 * Usage:
 *   <Alert tone="danger">Something went wrong.</Alert>
 */
// NOTE: layout is `block`, NOT flex. A flex container would treat each
// inline child (text node, <strong>, <em>) as its own flex item and lay
// them out in a row, splitting prose like "click <strong>X</strong> to do
// thing" into three columns. If we add an icon prop later, render the
// icon + body as siblings inside an explicit two-column flex inside this
// block - don't put flex on the root.
export const alertVariants = cva(
  [
    'block rounded-sm px-4 py-3',
    'text-sm font-sans leading-relaxed',
    'border',
  ].join(' '),
  {
    variants: {
      tone: {
        info: 'bg-info-bg border-info text-info-text',
        success: 'bg-teal-bg border-teal text-teal-text',
        warning: 'bg-warning-bg border-warning text-warning-text',
        danger: 'bg-danger-bg border-danger text-danger-text',
      },
    },
    defaultVariants: {
      tone: 'info',
    },
  },
);

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

export function Alert({ className, tone, children, ...props }: AlertProps) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      {children}
    </div>
  );
}
